import { FXSystem } from './engine/particles.ts';
import { LockInGame, GameResult } from './engine/game.ts';
import { sound } from './engine/audio.ts';
import { ChallengeSystem, ChallengeData } from './engine/challenge.ts';
import { PRNG } from './engine/prng.ts';
import { getRankInfo } from './engine/progression.ts';

// -------------------------------------------------------------
// Analytics Funnel Event Tracking
// -------------------------------------------------------------
export function logAnalyticsEvent(eventName: string, data?: Record<string, unknown>): void {
  try {
    const payload = {
      event: eventName,
      timestamp: Date.now(),
      ...data,
    };
    // Dispatch custom event for telemetry / test inspection
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('outaura_analytics', { detail: payload }));
    }
  } catch {
    // Non-blocking
  }
}

// -------------------------------------------------------------
// Player Profile & Guest Persistence Model
// -------------------------------------------------------------
export interface PlayerProfile {
  handle: string;
  totalAura: number;
  highScore: number;
  maxCombo: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  lastDailyDate?: string;
}

export function getStoredProfile(): PlayerProfile {
  if (typeof localStorage === 'undefined') {
    return {
      handle: 'Guest',
      totalAura: 0,
      highScore: 0,
      maxCombo: 0,
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
    };
  }

  const handle = localStorage.getItem('outaura_handle') || `Guest${Math.floor(1000 + Math.random() * 9000)}`;
  const totalAura = parseInt(localStorage.getItem('outaura_aura') || '0', 10);
  const highScore = parseInt(localStorage.getItem('outaura_highscore') || '0', 10);
  const maxCombo = parseInt(localStorage.getItem('outaura_maxcombo') || '0', 10);
  const gamesPlayed = parseInt(localStorage.getItem('outaura_games') || '0', 10);
  const wins = parseInt(localStorage.getItem('outaura_wins') || '0', 10);
  const losses = parseInt(localStorage.getItem('outaura_losses') || '0', 10);
  const lastDailyDate = localStorage.getItem('outaura_daily_date') || undefined;

  return { handle, totalAura, highScore, maxCombo, gamesPlayed, wins, losses, lastDailyDate };
}

export function saveProfile(p: PlayerProfile): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('outaura_handle', p.handle);
  localStorage.setItem('outaura_aura', String(p.totalAura));
  localStorage.setItem('outaura_highscore', String(p.highScore));
  localStorage.setItem('outaura_maxcombo', String(p.maxCombo));
  localStorage.setItem('outaura_games', String(p.gamesPlayed));
  localStorage.setItem('outaura_wins', String(p.wins));
  localStorage.setItem('outaura_losses', String(p.losses));
  if (p.lastDailyDate) {
    localStorage.setItem('outaura_daily_date', p.lastDailyDate);
  }
}

export interface MatchRecord {
  id: string;
  timestamp: number;
  score: number;
  maxCombo: number;
  auraDelta: number;
  seed: string;
  opponent: string | null;
  opponentScore: number | null;
  won: boolean;
  isDaily?: boolean;
}

export function getStoredHistory(): MatchRecord[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem('outaura_history') || '[]');
  } catch {
    return [];
  }
}

export function addHistoryRecord(rec: MatchRecord): void {
  if (typeof localStorage === 'undefined') return;
  const history = getStoredHistory();
  history.unshift(rec);
  if (history.length > 30) history.pop();
  localStorage.setItem('outaura_history', JSON.stringify(history));
}

// -------------------------------------------------------------
// Application Orchestration
// -------------------------------------------------------------
window.addEventListener('DOMContentLoaded', () => {
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  const tapZone = document.getElementById('tap-zone') as HTMLDivElement;
  const tapLabel = document.getElementById('tap-label') as HTMLDivElement;
  const tapIcon = document.getElementById('tap-icon') as HTMLDivElement;
  const tapSub = document.getElementById('tap-sub') as HTMLDivElement;

  // Header UI
  const playerNameDisplay = document.getElementById('player-name-display') as HTMLElement;
  const playerRankDisplay = document.getElementById('player-rank-display') as HTMLElement;
  const playerIdentityBtn = document.getElementById('player-identity-btn') as HTMLElement;
  const audioToggleBtn = document.getElementById('audio-toggle-btn') as HTMLButtonElement;
  const profileToggleBtn = document.getElementById('profile-toggle-btn') as HTMLButtonElement;
  const historyToggleBtn = document.getElementById('history-toggle-btn') as HTMLButtonElement;
  const dailyBadgeBtn = document.getElementById('daily-badge-btn') as HTMLElement;

  // HUD Elements
  const modeLabel = document.getElementById('mode-label') as HTMLElement;
  const timerText = document.getElementById('timer-text') as HTMLElement;
  const timerBar = document.getElementById('timer-bar') as HTMLElement;
  const hudScore = document.getElementById('hud-score') as HTMLElement;
  const comboBadge = document.getElementById('combo-badge') as HTMLElement;
  const comboText = document.getElementById('combo-text') as HTMLElement;

  // Challenge Banner
  const challengeBanner = document.getElementById('challenge-banner') as HTMLElement;
  const challengeCreatorName = document.getElementById('challenge-creator-name') as HTMLElement;
  const challengeTargetScore = document.getElementById('challenge-target-score') as HTMLElement;
  const challengeTargetCombo = document.getElementById('challenge-target-combo') as HTMLElement;

  // Intro Screen
  const introOverlay = document.getElementById('intro-overlay') as HTMLElement;
  const introChallengeBox = document.getElementById('intro-challenge-box') as HTMLElement;
  const introChallengeText = document.getElementById('intro-challenge-text') as HTMLElement;
  const introChallengeScore = document.getElementById('intro-challenge-score') as HTMLElement;
  const introChallengeCombo = document.getElementById('intro-challenge-combo') as HTMLElement;
  const introDailyBox = document.getElementById('intro-daily-box') as HTMLElement;
  const startGameBtn = document.getElementById('start-game-btn') as HTMLButtonElement;
  const switchModeBtn = document.getElementById('switch-mode-btn') as HTMLButtonElement;

  // Result Screen
  const resultOverlay = document.getElementById('result-overlay') as HTMLElement;
  const resultTitle = document.getElementById('result-title') as HTMLElement;
  const resultSubtitle = document.getElementById('result-subtitle') as HTMLElement;
  const resultPlayerHandle = document.getElementById('result-player-handle') as HTMLElement;
  const resultScore = document.getElementById('result-score') as HTMLElement;
  const resultAura = document.getElementById('result-aura') as HTMLElement;
  const statCombo = document.getElementById('stat-combo') as HTMLElement;
  const statPerfect = document.getElementById('stat-perfect') as HTMLElement;
  const statGreat = document.getElementById('stat-great') as HTMLElement;
  const statGood = document.getElementById('stat-good') as HTMLElement;
  const resultRankBadge = document.getElementById('result-rank-badge') as HTMLElement;
  const resultRankNext = document.getElementById('result-rank-next') as HTMLElement;
  const resultRankBar = document.getElementById('result-rank-bar') as HTMLElement;
  const cardPromptTxt = document.getElementById('card-prompt-txt') as HTMLElement;
  const copiedToast = document.getElementById('copied-toast') as HTMLElement;
  const shareBtn = document.getElementById('share-btn') as HTMLButtonElement;
  const revengeBtn = document.getElementById('revenge-btn') as HTMLButtonElement;
  const resultMenuBtn = document.getElementById('result-menu-btn') as HTMLButtonElement;
  const resultSeedTag = document.getElementById('result-seed-tag') as HTMLElement;

  // Profile Modal elements
  const profileModal = document.getElementById('profile-modal') as HTMLElement;
  const handleInput = document.getElementById('handle-input') as HTMLInputElement;
  const saveHandleBtn = document.getElementById('save-handle-btn') as HTMLButtonElement;
  const closeProfileBtn = document.getElementById('close-profile-btn') as HTMLButtonElement;
  const profileRankBadge = document.getElementById('profile-rank-badge') as HTMLElement;
  const profileTotalAura = document.getElementById('profile-total-aura') as HTMLElement;
  const profileRankBar = document.getElementById('profile-rank-bar') as HTMLElement;
  const profileRankNext = document.getElementById('profile-rank-next') as HTMLElement;
  const profileStatWins = document.getElementById('profile-stat-wins') as HTMLElement;
  const profileStatLosses = document.getElementById('profile-stat-losses') as HTMLElement;
  const profileStatWinrate = document.getElementById('profile-stat-winrate') as HTMLElement;
  const profileStatHighscore = document.getElementById('profile-stat-highscore') as HTMLElement;
  const profileStatMaxcombo = document.getElementById('profile-stat-maxcombo') as HTMLElement;
  const profileStatGames = document.getElementById('profile-stat-games') as HTMLElement;

  // Battle Log Modal elements
  const historyModal = document.getElementById('history-modal') as HTMLElement;
  const closeHistoryBtn = document.getElementById('close-history-btn') as HTMLButtonElement;
  const historyListContainer = document.getElementById('history-list-container') as HTMLElement;
  const historyStatWins = document.getElementById('history-stat-wins') as HTMLElement;
  const historyStatLosses = document.getElementById('history-stat-losses') as HTMLElement;
  const historyStatRate = document.getElementById('history-stat-rate') as HTMLElement;

  // Initialize Game & Systems
  let profile = getStoredProfile();
  updateProfileUI();

  const fx = new FXSystem();
  const game = new LockInGame(canvas, fx);

  let activeChallenge = ChallengeSystem.getActiveChallengeFromURL();
  let isDailyMode = !activeChallenge && ChallengeSystem.isDailyChallengeURL();
  let currentSeed = activeChallenge
    ? activeChallenge.seed
    : (isDailyMode ? ChallengeSystem.getDailySeed() : PRNG.generateSeedCode());
  let lastGameResult: GameResult | null = null;

  // Setup Challenge / Daily Banner in UI
  function syncModeUI(): void {
    if (activeChallenge) {
      logAnalyticsEvent('challenge_opened', {
        creator: activeChallenge.creator,
        targetScore: activeChallenge.score,
      });

      challengeBanner.classList.remove('hidden');
      challengeCreatorName.innerText = activeChallenge.creator;
      challengeTargetScore.innerText = activeChallenge.score.toLocaleString();
      challengeTargetCombo.innerText = String(activeChallenge.maxCombo || 0);

      introChallengeBox.classList.remove('hidden');
      introDailyBox.classList.add('hidden');
      introChallengeText.innerText = `${activeChallenge.creator.toUpperCase()} CHALLENGED YOU`;
      introChallengeScore.innerText = activeChallenge.score.toLocaleString();
      introChallengeCombo.innerText = `${activeChallenge.maxCombo || 0}x`;

      startGameBtn.innerText = 'BEAT THIS SCORE';
      switchModeBtn.innerText = 'PLAY SOLO RUN';
      modeLabel.innerText = `VS ${activeChallenge.creator.toUpperCase()}`;
    } else if (isDailyMode) {
      logAnalyticsEvent('daily_challenge_started');

      challengeBanner.classList.add('hidden');
      introChallengeBox.classList.add('hidden');
      introDailyBox.classList.remove('hidden');

      startGameBtn.innerText = 'START DAILY LOCK IN';
      switchModeBtn.innerText = 'PLAY SOLO RUN';
      modeLabel.innerText = 'DAILY LOCK IN';
    } else {
      challengeBanner.classList.add('hidden');
      introChallengeBox.classList.add('hidden');
      introDailyBox.classList.add('hidden');

      startGameBtn.innerText = 'START GAME';
      switchModeBtn.innerText = 'PLAY DAILY LOCK IN';
      modeLabel.innerText = 'LOCK IN WINDOW';
    }
  }

  syncModeUI();
  game.initGame(currentSeed, activeChallenge, isDailyMode);

  // Switch Mode button in intro screen
  switchModeBtn.addEventListener('click', () => {
    if (activeChallenge) {
      // Switch from challenge to solo
      activeChallenge = null;
      isDailyMode = false;
      currentSeed = PRNG.generateSeedCode();
    } else if (isDailyMode) {
      // Switch from daily to solo
      isDailyMode = false;
      currentSeed = PRNG.generateSeedCode();
    } else {
      // Switch from solo to daily
      isDailyMode = true;
      currentSeed = ChallengeSystem.getDailySeed();
    }
    syncModeUI();
    game.initGame(currentSeed, activeChallenge, isDailyMode);
  });

  // Daily badge in header
  dailyBadgeBtn.addEventListener('click', () => {
    activeChallenge = null;
    isDailyMode = true;
    currentSeed = ChallengeSystem.getDailySeed();
    syncModeUI();
    game.initGame(currentSeed, activeChallenge, isDailyMode);
    introOverlay.classList.remove('hidden');
    resultOverlay.classList.add('hidden');
  });

  // Audio Toggle
  audioToggleBtn.innerText = sound.getMuted() ? '🔇' : '🔊';
  audioToggleBtn.addEventListener('click', () => {
    const isMuted = sound.toggleMute();
    audioToggleBtn.innerText = isMuted ? '🔇' : '🔊';
  });

  // Profile Modal
  function openProfileModal(): void {
    const rank = getRankInfo(profile.totalAura);
    handleInput.value = profile.handle;
    profileRankBadge.innerText = `${rank.tier.badge} ${rank.tier.name.toUpperCase()}`;
    profileTotalAura.innerText = `${profile.totalAura.toLocaleString()} Aura`;
    profileRankBar.style.width = `${rank.progressPercent}%`;
    profileRankNext.innerText = rank.displayText;

    profileStatWins.innerText = String(profile.wins);
    profileStatLosses.innerText = String(profile.losses);
    const totalVersus = profile.wins + profile.losses;
    const winRate = totalVersus > 0 ? Math.round((profile.wins / totalVersus) * 100) : 0;
    profileStatWinrate.innerText = `${winRate}%`;
    profileStatHighscore.innerText = profile.highScore.toLocaleString();
    profileStatMaxcombo.innerText = `${profile.maxCombo}x`;
    profileStatGames.innerText = String(profile.gamesPlayed);

    profileModal.classList.remove('hidden');
  }

  playerIdentityBtn.addEventListener('click', openProfileModal);
  profileToggleBtn.addEventListener('click', openProfileModal);

  saveHandleBtn.addEventListener('click', () => {
    const newHandle = handleInput.value.trim().replace(/[<>]/g, '').slice(0, 16);
    if (newHandle) {
      profile.handle = newHandle;
      saveProfile(profile);
      updateProfileUI();
    }
    profileModal.classList.add('hidden');
  });

  closeProfileBtn.addEventListener('click', () => {
    profileModal.classList.add('hidden');
  });

  // Battle Log Modal
  function renderHistoryUI(): void {
    const history = getStoredHistory();
    historyStatWins.innerText = String(profile.wins);
    historyStatLosses.innerText = String(profile.losses);
    const totalVersus = profile.wins + profile.losses;
    const winRate = totalVersus > 0 ? Math.round((profile.wins / totalVersus) * 100) : 0;
    historyStatRate.innerText = `${winRate}%`;

    if (history.length === 0) {
      historyListContainer.innerHTML = '<div class="empty-history">No matches played yet. Go play a round!</div>';
      return;
    }

    historyListContainer.innerHTML = history.map((item) => {
      const dateStr = new Date(item.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      let vsText = 'Solo Run';
      let outcomeTag = '<span class="history-tag solo">SOLO</span>';
      let itemClass = 'history-item';

      if (item.opponent) {
        vsText = `${profile.handle} vs ${item.opponent}`;
        if (item.won) {
          outcomeTag = '<span class="history-tag win">WIN</span>';
          itemClass = 'history-item win';
        } else {
          outcomeTag = '<span class="history-tag loss">LOSS</span>';
          itemClass = 'history-item loss';
        }
      } else if (item.isDaily) {
        vsText = '📅 Daily Lock In';
        outcomeTag = '<span class="history-tag win">DAILY</span>';
      }

      const scoreDetail = item.opponentScore !== null
        ? `${item.score.toLocaleString()} — ${item.opponentScore.toLocaleString()}`
        : `${item.score.toLocaleString()} pts`;

      return `
        <div class="${itemClass}">
          <div class="history-meta">
            <span class="history-score">${scoreDetail}</span>
            <span class="history-sub">${vsText} • ${dateStr}</span>
          </div>
          <div class="history-actions">
            <span style="font-size: 0.75rem; color: var(--aura-emerald); font-weight: 800;">+${item.auraDelta}</span>
            ${outcomeTag}
          </div>
        </div>
      `;
    }).join('');
  }

  historyToggleBtn.addEventListener('click', () => {
    renderHistoryUI();
    historyModal.classList.remove('hidden');
  });

  closeHistoryBtn.addEventListener('click', () => {
    historyModal.classList.add('hidden');
  });

  function updateProfileUI(): void {
    const rank = getRankInfo(profile.totalAura);
    playerNameDisplay.innerText = profile.handle;
    playerRankDisplay.innerText = rank.tier.name;
  }

  // Tap handling (Universal pointer / touch / space)
  function onInputTap(e?: Event): void {
    if (e) {
      e.preventDefault();
    }
    if (game.state === 'IDLE' && !introOverlay.classList.contains('hidden')) {
      return; // Must press Start Game first to unlock AudioContext cleanly
    }
    game.handleTap();
  }

  tapZone.addEventListener('pointerdown', onInputTap);
  canvas.addEventListener('pointerdown', onInputTap);

  window.addEventListener('keydown', (e) => {
    if (e.code === 'Space' && !e.repeat) {
      e.preventDefault();
      onInputTap();
    }
  });

  // Start Game Button
  startGameBtn.addEventListener('click', () => {
    introOverlay.classList.add('hidden');
    game.start();
    logAnalyticsEvent('game_started', {
      seed: game.seed,
      isDaily: game.isDaily,
      hasOpponent: !!game.opponent,
    });
  });

  // Game Over Callback
  game.setGameOverCallback((result: GameResult) => {
    lastGameResult = result;

    logAnalyticsEvent('game_completed', {
      score: result.score,
      maxCombo: result.maxCombo,
      auraGained: result.auraDelta,
      won: result.outauraWon,
    });

    // Update player profile
    profile.gamesPlayed++;
    profile.totalAura += result.auraDelta;
    if (result.score > profile.highScore) {
      profile.highScore = result.score;
    }
    if (result.maxCombo > profile.maxCombo) {
      profile.maxCombo = result.maxCombo;
    }
    if (result.opponent) {
      if (result.outauraWon) {
        profile.wins++;
        logAnalyticsEvent('challenge_won', { opponent: result.opponent.creator });
      } else {
        profile.losses++;
        logAnalyticsEvent('challenge_lost', { opponent: result.opponent.creator });
      }
    }
    saveProfile(profile);
    updateProfileUI();

    // Record into match history
    addHistoryRecord({
      id: `match_${Date.now()}`,
      timestamp: Date.now(),
      score: result.score,
      maxCombo: result.maxCombo,
      auraDelta: result.auraDelta,
      seed: result.seed,
      opponent: result.opponent ? result.opponent.creator : null,
      opponentScore: result.opponent ? result.opponent.score : null,
      won: result.outauraWon,
      isDaily: result.isDaily,
    });

    // Update Result Screen Headlines
    if (result.opponent) {
      if (result.outauraWon) {
        resultTitle.innerText = `YOU OUTAURA'D ${result.opponent.creator.toUpperCase()} 👑`;
        resultTitle.className = 'result-headline win';
        const diff = result.score - result.opponent.score;
        resultSubtitle.innerText = `Beat ${result.opponent.creator} by +${diff.toLocaleString()} pts! The Aura is yours.`;
        revengeBtn.innerText = 'CHALLENGE THEM BACK 🔁';
      } else {
        resultTitle.innerText = `${result.opponent.creator.toUpperCase()} STILL HAS MORE AURA 💀`;
        resultTitle.className = 'result-headline loss';
        const diff = result.opponent.score - result.score;
        resultSubtitle.innerText = `${result.opponent.creator} beat you by ${diff.toLocaleString()} pts. Don't let it slide.`;
        revengeBtn.innerText = 'REVENGE 😈';
      }
      cardPromptTxt.innerText = `THINK YOU HAVE MORE AURA THAN ${profile.handle.toUpperCase()}?`;
    } else {
      if (result.score >= 10000) {
        resultTitle.innerText = 'MYTHIC AURA GOD 👑';
        resultTitle.className = 'result-headline win';
        resultSubtitle.innerText = 'Ascended performance. Untouchable.';
      } else if (result.score >= 7000) {
        resultTitle.innerText = 'LOCKED IN DEMON 🔥';
        resultTitle.className = 'result-headline win';
        resultSubtitle.innerText = 'God-tier precision timing.';
      } else if (result.score >= 4000) {
        resultTitle.innerText = 'SOLID PERFORMANCE ⚡';
        resultTitle.className = 'result-headline win';
        resultSubtitle.innerText = 'Main Character energy unlocked.';
      } else {
        resultTitle.innerText = 'CERTIFIED NPC 😭';
        resultTitle.className = 'result-headline loss';
        resultSubtitle.innerText = 'Bro lost Aura. Run it back.';
      }
      revengeBtn.innerText = 'RUN IT BACK 🔁';
      cardPromptTxt.innerText = 'THINK YOU HAVE MORE AURA?';
    }

    resultPlayerHandle.innerText = profile.handle.toUpperCase();
    resultScore.innerText = result.score.toLocaleString();
    resultAura.innerText = `+${result.auraDelta.toLocaleString()} AURA GAINED`;

    statCombo.innerText = `${result.maxCombo}x`;
    statPerfect.innerText = String(result.perfectHits);
    statGreat.innerText = String(result.greatHits);
    statGood.innerText = String(result.goodHits);

    // Rank Progress on Result Screen
    const rank = getRankInfo(profile.totalAura);
    resultRankBadge.innerText = `${rank.tier.badge} ${rank.tier.name.toUpperCase()}`;
    resultRankNext.innerText = rank.displayText;
    resultRankBar.style.width = `${rank.progressPercent}%`;

    resultSeedTag.innerText = `SEED: ${result.seed}`;
    copiedToast.innerText = '';

    resultOverlay.classList.remove('hidden');
  });

  // Revenge / Run It Back Button
  revengeBtn.addEventListener('click', () => {
    resultOverlay.classList.add('hidden');

    if (lastGameResult && lastGameResult.opponent) {
      logAnalyticsEvent('revenge_started', { opponent: lastGameResult.opponent.creator });
    }

    // Replay with current seed or generate fresh seed if desired
    game.initGame(currentSeed, activeChallenge, isDailyMode);
    game.start();
  });

  // Share Challenge Button
  shareBtn.addEventListener('click', async () => {
    if (!lastGameResult) return;

    const challengeData: ChallengeData = {
      seed: lastGameResult.seed,
      creator: profile.handle,
      score: lastGameResult.score,
      maxCombo: lastGameResult.maxCombo,
      ver: 2,
    };

    copiedToast.innerText = 'Generating challenge...';
    const res = await ChallengeSystem.shareChallenge(challengeData);

    logAnalyticsEvent('challenge_created', {
      score: challengeData.score,
      combo: challengeData.maxCombo,
    });

    if (res.copied) {
      copiedToast.innerText = 'Challenge link copied 🔥 Send to a friend!';
    } else if (res.success) {
      copiedToast.innerText = 'Shared! Waiting for opponent 👑';
    } else {
      copiedToast.innerText = 'Challenge link ready!';
    }

    setTimeout(() => {
      copiedToast.innerText = '';
    }, 4000);
  });

  // Main Menu Button on Result Screen
  resultMenuBtn.addEventListener('click', () => {
    resultOverlay.classList.add('hidden');
    introOverlay.classList.remove('hidden');
    // Generate fresh seed for next solo run
    activeChallenge = null;
    isDailyMode = false;
    currentSeed = PRNG.generateSeedCode();
    syncModeUI();
    game.initGame(currentSeed, activeChallenge, isDailyMode);
  });

  // Main Render & Animation Loop (Targeting solid 60 FPS)
  function gameLoop(now: number): void {
    game.update(now);
    game.render();

    // Update HUD state
    if (game.state === 'PLAYING') {
      const remaining = Math.max(0, game.getTimeRemaining());
      timerText.innerText = `${remaining.toFixed(1)}s`;

      const pct = (remaining / game.TOTAL_TIME) * 100;
      timerBar.style.width = `${pct}%`;
      if (remaining <= 5.0) {
        timerBar.classList.add('critical');
      } else {
        timerBar.classList.remove('critical');
      }

      hudScore.innerText = game.score.toLocaleString();

      const mult = game.getMultiplier();
      if (game.combo >= 20) {
        comboBadge.className = 'combo-badge overdrive';
        comboText.innerText = `👑 x${mult} MYTHIC (${game.combo})`;
        tapZone.classList.add('locked-in');
        tapLabel.innerText = '👑 MYTHIC AURA FRENZY';
        tapIcon.innerText = '👑';
        tapSub.innerText = 'Max 5x Multiplier Active!';
      } else if (game.combo >= 15) {
        comboBadge.className = 'combo-badge overdrive';
        comboText.innerText = `⚡ x${mult} AURA DEMON (${game.combo})`;
        tapZone.classList.add('locked-in');
        tapLabel.innerText = '⚡ AURA DEMON RAMPAGE';
        tapIcon.innerText = '😈';
        tapSub.innerText = '4x Multiplier Active!';
      } else if (game.combo >= 10) {
        comboBadge.className = 'combo-badge locked-in';
        comboText.innerText = `🔥 x${mult} OVERDRIVE (${game.combo})`;
        tapZone.classList.add('locked-in');
        tapLabel.innerText = '🔥 LOCKED IN OVERDRIVE';
        tapIcon.innerText = '⚡';
        tapSub.innerText = '3x Multiplier Active!';
      } else if (game.combo >= 5) {
        comboBadge.className = 'combo-badge locked-in';
        comboText.innerText = `🔥 x${mult} LOCKED IN (${game.combo})`;
        tapZone.classList.add('locked-in');
        tapLabel.innerText = '🔥 LOCKED IN';
        tapIcon.innerText = '🔥';
        tapSub.innerText = '2x Multiplier Active!';
      } else if (game.combo > 0) {
        comboBadge.className = 'combo-badge active';
        comboText.innerText = `COMBO ${game.combo}`;
        tapZone.classList.remove('locked-in');
        tapLabel.innerText = 'TAP TO LOCK IN';
        tapIcon.innerText = '🎯';
        tapSub.innerText = 'Hit the marker inside the sweetspot';
      } else {
        comboBadge.className = 'combo-badge';
        comboText.innerText = 'COMBO 0';
        tapZone.classList.remove('locked-in');
        tapLabel.innerText = 'TAP TO LOCK IN';
        tapIcon.innerText = '🎯';
        tapSub.innerText = 'Hit the marker inside the sweetspot';
      }
    }

    requestAnimationFrame(gameLoop);
  }

  requestAnimationFrame(gameLoop);

  // Register PWA Service Worker
  if ('serviceWorker' in navigator && window.location.protocol.startsWith('http')) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  }
});
